import { useEffect, useCallback, useState, useRef, memo } from 'react';
import {
  Box,
  TextField,
  InputAdornment,
  IconButton,
  Typography,
  CircularProgress,
  Button,
  ButtonGroup,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import CloseIcon from '@mui/icons-material/Close';
import ShuffleIcon from '@mui/icons-material/Shuffle';
import SelectAllIcon from '@mui/icons-material/SelectAll';
import { useBandStore } from '../../stores/bandStore';
import { useSettingsStore } from '../../stores/settingsStore';
import { usePlayerStore } from '../../stores/playerStore';
import { useFilteredBands } from '../../hooks/useFilteredBands';
import BandTile from './BandTile';

const BandList = () => {
  // Ref for search input focus
  const searchInputRef = useRef<HTMLInputElement>(null);
  
  // Optimized selectors - only subscribe to what we need
  const bands = useBandStore((state) => state.bands);
  const searchVal = useBandStore((state) => state.searchVal);
  const showSearch = useBandStore((state) => state.showSearch);
  const isRefreshing = useBandStore((state) => state.isRefreshing);
  const selectedBandsSize = useBandStore((state) => state.selectedBands.size);

  const fetchBands = useBandStore((state) => state.fetchBands);
  const searchValUpdate = useBandStore((state) => state.searchValUpdate);
  const toggleSearch = useBandStore((state) => state.toggleSearch);
  const loadFavoriteArtists = useBandStore((state) => state.loadFavoriteArtists);
  const loadFeaturedBands = useBandStore((state) => state.loadFeaturedBands);
  const randomizeArtists = useBandStore((state) => state.randomizeArtists);
  const selectAllBands = useBandStore((state) => state.selectAllBands);
  const clearSelectedBands = useBandStore((state) => state.clearSelectedBands);

  const {
    fetchMoreSongs,
    setQueueAndBeginPlaying,
    clearUpcomingSongs,
    updateQueue,
    isFetchingSong,
  } = usePlayerStore();

  const { shouldLoadPreviousBands } = useSettingsStore();

  const filteredBands = useFilteredBands();

  // Debounced search
  const [localSearchVal, setLocalSearchVal] = useState(searchVal);
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const handleSearchChange = useCallback((value: string) => {
    setLocalSearchVal(value);
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }
    debounceTimer.current = setTimeout(() => {
      searchValUpdate(value);
    }, 300);
  }, [searchValUpdate]);

  useEffect(() => {
    const initializeData = async () => {
      await fetchBands();
      if (bands.length > 0) {
        await loadFeaturedBands();
        if (shouldLoadPreviousBands) {
          await loadFavoriteArtists();
        }
      }
    };

    initializeData();
  }, [bands.length, shouldLoadPreviousBands, fetchBands, loadFeaturedBands, loadFavoriteArtists]);

  // Focus search input when it appears
  useEffect(() => {
    if (showSearch && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [showSearch]);



  const handleRandomize = useCallback(async () => {
    await randomizeArtists();
  }, [randomizeArtists]);

  const handleSelectAll = useCallback(() => {
    selectAllBands();
  }, [selectAllBands]);

  const handleClear = useCallback(() => {
    clearSelectedBands();
  }, [clearSelectedBands]);

  const handleSetActive = useCallback(async () => {
    if (selectedBandsSize === 0) return;

    try {
      const { isPlaying, queue, currentIndex } = usePlayerStore.getState();
      const hasCurrentSong = currentIndex >= 0 && currentIndex < queue.length;

      if (isPlaying && hasCurrentSong) {
        // Replace upcoming songs while keeping current track playing
        await clearUpcomingSongs();
      } else {
        // Fresh start: clear queue, fetch songs with all modes respected, then play
        updateQueue([]);
        await fetchMoreSongs(5);
        await setQueueAndBeginPlaying();
      }
    } catch (error) {
      console.error('Error setting active bands:', error);
      if (error instanceof Error && error.name === 'NotSupportedError') {
        console.error('Audio format not supported. The song URL may be invalid or unavailable.');
      }
    }
  }, [selectedBandsSize, fetchMoreSongs, setQueueAndBeginPlaying, clearUpcomingSongs, updateQueue]);

  if (bands.length === 0 && !isRefreshing) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 500, fontSize: '1.125rem' }}>
            Available Bands
          </Typography>
          <IconButton onClick={toggleSearch} size="small">
            {showSearch ? <CloseIcon /> : <SearchIcon />}
          </IconButton>
        </Box>

        {/* Search Bar */}
        {showSearch && (
          <TextField
            fullWidth
            variant="outlined"
            placeholder="Search bands..."
            value={localSearchVal}
            onChange={(e) => handleSearchChange(e.target.value)}
            size="small"
            inputRef={searchInputRef}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" fontSize="small" />
                </InputAdornment>
              ),
              endAdornment: searchVal && (
                <InputAdornment position="end">
                  <IconButton onClick={() => handleSearchChange('')} size="small">
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{ 
              mb: 2,
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                bgcolor: 'background.paper',
              }
            }}
          />
        )}

        {/* Action Buttons */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 400 }}>
            {filteredBands.length} bands
          </Typography>

          <ButtonGroup variant="outlined" size="small">
            <Button 
              startIcon={<ShuffleIcon fontSize="small" />} 
              onClick={handleRandomize}
              sx={{ textTransform: 'none' }}
            >
              Randomize
            </Button>
            <Button 
              startIcon={<SelectAllIcon fontSize="small" />} 
              onClick={handleSelectAll}
              sx={{ textTransform: 'none' }}
            >
              Select All
            </Button>
            <Button 
              onClick={handleClear}
              sx={{ textTransform: 'none' }}
            >
              Clear
            </Button>
          </ButtonGroup>
        </Box>

        {/* Start Playing Button */}
        {selectedBandsSize > 0 && (
          <Button
            variant="contained"
            onClick={handleSetActive}
            disabled={isFetchingSong}
            fullWidth
            size="medium"
            sx={{
              bgcolor: 'primary.main',
              textTransform: 'none',
              fontWeight: 500,
              py: 1,
              borderRadius: 2,
            }}
          >
            {isFetchingSong ? 'Loading...' : `Start Playing (${selectedBandsSize} bands)`}
          </Button>
        )}
      </Box>

      {/* Band List */}
      <Box sx={{ flex: 1, overflow: 'auto', p: 1 }}>
        {filteredBands.length === 0 && searchVal ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              No bands found
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Try adjusting your search
            </Typography>
          </Box>
        ) : (
          <Box sx={{ minHeight: '100%' }}>
            {filteredBands.map((band) => (
              <BandTile key={`${band.collection}|${band.source}|${band.bandIdx}`} band={band} />
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default memo(BandList);
